import { put, call, delay } from 'redux-saga/effects';
import { takeLatest } from 'redux-saga/effects';
import * as types from './constants';
import * as actions from './actions';

import { CustomNotification } from 'common/components';

import * as services from 'services/maintenanceForm';
import { forwardTo, forwardToAndReplace } from 'utils/common/route';

import * as maintenanceFormActions from './actions';
import * as globalActions from '@redux/global/actions';

import {
  confirmDeleteFormItem,
  confirmCloneFormItem,
} from 'pages/maintenance-form/utils';

export function* updateNewFormTemplate(payload) {
  try {
    const response = yield call(
      services.updateNewFormTemplateService,
      payload.formData
    );
    if (!response.isSuccess) {
      yield put(actions.updateNewFormTemplateError(response.message));
    } else {
      yield put(actions.updateNewFormTemplateSuccess());

      const formFormat = response.data?.formFormat.toLowerCase();
      const typeParam = formFormat.includes('doc')
        ? 'word'
        : formFormat.includes('xls')
        ? 'excel'
        : formFormat.includes('ppt')
        ? 'powerpoint'
        : null;

      if (!typeParam) return;
      yield call(
        forwardToAndReplace,
        `/maintenance/form/${typeParam}/${response.data.formId}`
      );
    }
  } catch (error) {
    yield put(actions.updateNewFormTemplateError(error));
  }
}

export function* getFormTemplateDetail(payload) {
  try {
    const response = yield call(services.getFormTemplateDetailService, payload);
    if (!response.isSuccess) {
      yield put(actions.getFormTemplateDetailError(response.message));
    } else {
      yield put(actions.getFormTemplateDetailSuccess(response.data));
    }
  } catch (error) {
    yield put(actions.getFormTemplateDetailError(error));
  }
}

export function* getFormDetailSaga(action) {
  try {
    yield put(maintenanceFormActions.toggleViewLoading(true));
    const response = yield call(services.getFormDetail, action.payload);
    yield put(maintenanceFormActions.toggleViewLoading(false));

    const { data, isSuccess, message } = response;
    if (isSuccess) {
      yield put(maintenanceFormActions.getFormDetailSuccess(data));
      yield delay(1000);
      yield put(maintenanceFormActions.toggleRefreshDataPointList());
    } else {
      yield put(maintenanceFormActions.getFormDetailFail(message));
    }
  } catch (error) {
    yield put(maintenanceFormActions.getFormDetailFail(error));
  }
}

export function* getExportedFormShortDetailSaga(action) {
  try {
    const response = yield call(
      services.getExportedFormShortDetail,
      action.payload
    );

    const { data, isSuccess, message } = response;
    if (isSuccess) {
      yield put(maintenanceFormActions.getExportedFormShortDetailSuccess(data));
    } else {
      yield put(maintenanceFormActions.getExportedFormShortDetailFail(message));
    }
  } catch (error) {
    yield put(maintenanceFormActions.getExportedFormShortDetailFail(error));
  }
}

export function* replaceFormFileSaga(action) {
  const { formId, newFile } = action.payload;
  const fileType = newFile.name.split('.').pop();

  const fileTypeParams = fileType.includes('doc')
    ? 'word'
    : fileType.includes('xls')
    ? 'excel'
    : fileType.includes('ppt')
    ? 'powerpoint'
    : null;

  if (!fileTypeParams) return;

  yield call(forwardTo, `/maintenance/form/${fileTypeParams}/${formId}`);
  yield delay(1500);
  yield call(getFormDetailSaga, { payload: formId });
}

export function* editFormTemplateSaga(payload) {
  try {
    yield delay(16000);
    const response = yield call(
      services.editFormTemplateService,
      payload.params
    );
    if (!response.isSuccess) {
      yield call(CustomNotification.error, response.message);
      yield put(actions.editFormTemplateFail(response.message));
    } else {
      CustomNotification.success('Save Form successfully');
      yield put(maintenanceFormActions.getFormDetailSuccess({}));
      yield put(actions.updateSelectedDataPoint([]));
      yield put(maintenanceFormActions.getFormDetail(payload?.params?.formId));
      yield delay(500);
      yield put(actions.editFormTemplateSuccess(response.data));
    }
  } catch (error) {
    const errorMessage = 'Failed to save the form template';
    yield call(CustomNotification.error, errorMessage);
    yield put(actions.editFormTemplateFail(errorMessage));
  }
}

export function* deleteFormItemSaga(action) {
  try {
    const formIdList = action.payload;
    const body = { formIds: formIdList };

    const response = yield call(confirmDeleteFormItem, body);

    const { isSuccess } = response;

    if (isSuccess) {
      yield delay(1000);
      yield call(CustomNotification.success, 'Delete form(s) successfully');

      if (window.location.pathname === `/maintenance/form`) {
        yield call(reloadGridSaga);
      } else {
        yield call(forwardTo, `/maintenance/form`);
        yield put(maintenanceFormActions.getFormDetailSuccess(null));
      }
    }
  } catch (e) {}
}

export function* reloadGridSaga() {
  yield put(globalActions.updateReloadPage(true));
  yield delay(1500);
  yield put(globalActions.updateReloadPage(false));
}

export function* toggleRefreshDataPointListSaga() {
  yield put(maintenanceFormActions.toggleRefreshDataPointListSuccess(true));
  yield delay(500);
  yield put(maintenanceFormActions.toggleRefreshDataPointListSuccess(false));
}

export function* cloneFormSaga(action) {
  try {
    const response = yield call(confirmCloneFormItem, action.payload);

    const { isSuccess } = response;

    if (!isSuccess) {
      yield put(actions.cloneFormItemFail(response.message));
    } else {
      CustomNotification.success('Clone form successfully');
      yield put(actions.cloneFormItemSuccess());
      yield call(reloadGridSaga);
    }
  } catch (error) {
    yield put(actions.cloneFormItemFail(error));
  }
}

export function* downloadExportedFormSaga(action) {
  try {
    yield call(services.downloadExportedForm, action.payload);
  } catch (error) {
    yield call(CustomNotification.error, 'Failed to download the expoted form');
  }
}

export function* replaceFormTemplateFile(payload) {
  try {
    yield call(services.replaceFormTemplateFileService, payload.formData);
  } catch (error) {}
}

export function* refreshDataPointDetailSaga() {
  yield put(actions.refreshDataPointDetailSuccess(true));
  yield delay(1000);
  yield put(actions.refreshDataPointDetailSuccess(false));
}

export default function* watchMaintenceForm() {
  yield takeLatest(types.UPDATE_NEW_FORM_TEMPLATE, updateNewFormTemplate);
  yield takeLatest(types.GET_FORM_DETAIL, getFormDetailSaga);
  yield takeLatest(
    types.FORM_MANAGE_GET_EXPOTED_FORM_SHORT_DETAIL,
    getExportedFormShortDetailSaga
  );
  yield takeLatest(types.GET_FORM_TEMPLATE_DETAIL, getFormTemplateDetail);
  yield takeLatest(types.EDIT_FORM_TEMPLATE, editFormTemplateSaga);
  yield takeLatest(
    types.FORM_MANAGE_REPLACE_FORM_FILE_SUCCESS,
    replaceFormFileSaga
  );
  yield takeLatest(types.FORM_MANAGE_DELETE_FORM_ITEM, deleteFormItemSaga);
  yield takeLatest(
    types.FORM_MANAGE_TOGGLE_REFRESH_DATA_POINT_LIST,
    toggleRefreshDataPointListSaga
  );
  yield takeLatest(types.FORM_MANAGE_CLONE_FORM_ITEM, cloneFormSaga);
  yield takeLatest(types.REPLACE_FORM_TEMPLATE_FILE, replaceFormTemplateFile);
  yield takeLatest(types.REFRESH_DATA_POINT_DETAIL, refreshDataPointDetailSaga);
  yield takeLatest(
    types.FORM_MANAGE_DOWNLOAD_EXPORTED_FORM,
    downloadExportedFormSaga
  );
}
